import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import useFitnessProfileChallenges from '../../../hooks/useFitnessProfileChallenges';
import FitnessProfileBloodPressureLogModal from '../../FitnessProfile/FitnessProfileBloodPressureLogModal';
import FitnessProfileEditModal from '../../FitnessProfile/FitnessProfileEditModal';
import FitnessProfileGoalsModal from '../../FitnessProfile/FitnessProfileGoalsModal';
import FitnessProfileGoalProgressModal from '../../FitnessProfile/FitnessProfileGoalProgressModal';
import FitnessProfileReadingsManageModal from '../../FitnessProfile/FitnessProfileReadingsManageModal';
import FitnessProfileWeightLogModal from '../../FitnessProfile/FitnessProfileWeightLogModal';
import FitnessProfileBloodPressureTrackingSection from '../../FitnessProfile/sections/FitnessProfileBloodPressureTrackingSection';
import FitnessProfileCurrentHabitsSection from '../../FitnessProfile/sections/FitnessProfileCurrentHabitsSection';
import FitnessProfileSettingsSection from '../../FitnessProfile/sections/FitnessProfileSettingsSection';
import FitnessProfileGoalsSection from '../../FitnessProfile/sections/FitnessProfileGoalsSection';
import FitnessProfileMetricsRow from '../../FitnessProfile/sections/FitnessProfileMetricsRow';
import FitnessProfileThisWeekSection from '../../FitnessProfile/sections/FitnessProfileThisWeekSection';
import FitnessProfileChallengesSection from '../../FitnessProfile/sections/FitnessProfileChallengesSection';
import FitnessProfileAiTrainerSection from '../../FitnessProfile/sections/FitnessProfileAiTrainerSection';
import FitnessProfileWeightTrackingSection from '../../FitnessProfile/sections/FitnessProfileWeightTrackingSection';
import FitnessProfilePrimaryBadge from '../../FitnessProfile/shared/FitnessProfilePrimaryBadge';
import { LogReadingEditButton } from '../../FitnessProfile/shared/FitnessProfileReadingActions';
import useFitnessProfileDetailPage from '../../../hooks/useFitnessProfileDetailPage';

function FitnessProfileDetailLoading() {
	return (
		<div className="container p-4 pt-4 lg:py-14 md:px-20">
			<p className="text-sm text-gray-500 dark:text-gray-400">Loading profile…</p>
		</div>
	);
}

function FitnessProfileDetailNotFound({ canUseApi, profileError }) {
	return (
		<div className="container p-4 pt-4 lg:py-14 md:px-20">
			<div className="bg-white dark:bg-neutral-900 border border-gray-300 dark:border-neutral-600 rounded-lg p-6">
				<h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
					{!canUseApi ? 'Sign in required' : 'Fitness profile not found'}
				</h2>
				<p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
					{!canUseApi
						? 'Sign in to view fitness profiles and log readings.'
						: profileError || 'Choose a profile from the list or create one.'}
				</p>
				<Link
					to="/fitness-profiles"
					className="mt-4 inline-block text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline"
				>
					Go to Fitness Profiles
				</Link>
			</div>
		</div>
	);
}

function FitnessProfileDetailHeader({ profile, canEditProfile, onEdit }) {
	return (
		<div className="mt-4 mb-3 lg:mb-6 flex items-center justify-between gap-4">
			<div className="flex min-w-0 flex-wrap items-center gap-2.5">
				<h1 className="min-w-0 truncate text-xl sm:text-2xl md:text-4xl font-semibold leading-none text-gray-900 dark:text-dark-text">
					{profile.name}
				</h1>
				{profile.is_primary && <FitnessProfilePrimaryBadge />}
				{canEditProfile && (
					<span className="inline-flex shrink-0 -translate-y-0.5 md:-translate-y-1">
						<LogReadingEditButton onClick={onEdit} ariaLabel="Edit profile" title="Edit profile" />
					</span>
				)}
			</div>
			<Link
				to="/fitness-profiles"
				className="shrink-0 text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline"
			>
				← Fitness profiles
			</Link>
		</div>
	);
}

export default function FitnessProfileDetailPage() {
	const { id } = useParams();
	const { user, csrfToken, endpoints } = useAuth();
	const page = useFitnessProfileDetailPage(id);
	const challenges = useFitnessProfileChallenges({
		user,
		csrfToken,
		endpoints,
		fitnessProfileId: id,
		isPrimary: Boolean(page.profile?.is_primary),
		enabled: page.canUseApi && !page.profileLoading && Boolean(page.profile)
	});

	if (page.profileLoading) {
		return <FitnessProfileDetailLoading />;
	}

	if (!page.profile) {
		return (
			<FitnessProfileDetailNotFound
				canUseApi={page.canUseApi}
				profileError={page.profileError}
			/>
		);
	}

	const {
		profile,
		editModal,
		goalsModal,
		goalProgressModal,
		bloodPressureLog,
		bloodPressureManage,
		weightLog,
		weightManage
	} = page;

	return (
		<div className="container p-4 pt-4 lg:py-14 md:px-20">
			<FitnessProfileDetailHeader
				profile={profile}
				canEditProfile={page.canEditProfile}
				onEdit={editModal.open}
			/>

			<FitnessProfileMetricsRow
				profile={profile}
				weightDisplay={page.weightDisplay}
				bloodPressureDisplay={page.bloodPressureDisplay}
			/>

			<div className="mt-4 grid grid-cols-1 lg:grid-cols-3 gap-4">
				<FitnessProfileWeightTrackingSection
					canLogReadings={page.canUseApi}
					chart={page.weightChart}
					weightGoal={page.weightGoal}
					onManage={weightManage.open}
					onLog={weightLog.open}
				/>
				<FitnessProfileBloodPressureTrackingSection
					canLogReadings={page.canUseApi}
					chart={page.bloodPressureChart}
					bloodPressureGoal={page.bloodPressureGoal}
					onManage={bloodPressureManage.open}
					onLog={bloodPressureLog.open}
				/>
				<FitnessProfileGoalsSection
					canEdit={page.canEditProfile}
					goals={profile.goals || []}
					onManage={goalsModal.openManage}
					onAdd={goalsModal.openAdd}
					onUpdateProgress={goalProgressModal.open}
				/>
			</div>

			<FitnessProfileChallengesSection
				isPrimary={profile.is_primary}
				profileName={profile.name}
				challenges={challenges.challenges}
				loading={challenges.loading}
				error={challenges.error}
				actionError={challenges.actionError}
				onClearActionError={() => challenges.setActionError('')}
				onRunAction={challenges.runAction}
				onDayCheck={challenges.handleDayCheck}
				isDayCheckPending={challenges.isDayCheckPending}
			/>

			<FitnessProfileAiTrainerSection profile={profile} />

			<FitnessProfileCurrentHabitsSection questionnaire={profile.current_questionnaire} />
			{!profile.is_primary && (
				<FitnessProfileSettingsSection
					fitnessHubsOnly={page.profileSettings.fitnessHubsOnly}
					onFitnessHubsOnlyChange={page.profileSettings.onFitnessHubsOnlyChange}
					canEdit={page.canEditProfile}
					saving={page.profileSettings.saving}
					error={page.profileSettings.error}
				/>
			)}
			<FitnessProfileThisWeekSection profile={profile} />

			<FitnessProfileEditModal
				isOpen={editModal.isOpen}
				profile={profile}
				onClose={editModal.close}
				onSave={async (payload) => {
					await editModal.save(payload);
					challenges.loadChallenges();
				}}
				saving={editModal.saving}
				error={editModal.error}
			/>
			<FitnessProfileGoalsModal
				mode={goalsModal.mode}
				isOpen={goalsModal.isOpen}
				goals={profile.goals || []}
				hasWeightReading={goalsModal.hasWeightReading}
				hasBloodPressureReading={goalsModal.hasBloodPressureReading}
				onClose={goalsModal.close}
				onSave={goalsModal.save}
				saving={goalsModal.saving}
				error={goalsModal.error}
			/>
			<FitnessProfileGoalProgressModal
				isOpen={goalProgressModal.isOpen}
				goal={goalProgressModal.goal}
				onClose={goalProgressModal.close}
				onSave={goalProgressModal.save}
				saving={goalProgressModal.saving}
				error={goalProgressModal.error}
			/>
			<FitnessProfileBloodPressureLogModal
				isOpen={bloodPressureLog.isOpen}
				onClose={bloodPressureLog.close}
				onSave={bloodPressureLog.save}
				form={bloodPressureLog.form}
				onFormChange={bloodPressureLog.onFormChange}
				error={bloodPressureLog.error}
				saving={bloodPressureLog.saving}
			/>
			<FitnessProfileWeightLogModal
				isOpen={weightLog.isOpen}
				onClose={weightLog.close}
				onSave={weightLog.save}
				form={weightLog.form}
				onFormChange={weightLog.onFormChange}
				error={weightLog.error}
				saving={weightLog.saving}
			/>
			<FitnessProfileReadingsManageModal
				isOpen={bloodPressureManage.isOpen}
				onClose={bloodPressureManage.close}
				title="Blood Pressure Readings"
				emptyMessage="No blood pressure readings yet."
				items={bloodPressureManage.items}
				onEdit={bloodPressureManage.onEdit}
				onDelete={bloodPressureManage.onDelete}
				deletingId={bloodPressureManage.deletingId}
				error={bloodPressureManage.error}
			/>
			<FitnessProfileReadingsManageModal
				isOpen={weightManage.isOpen}
				onClose={weightManage.close}
				title="Weight Readings"
				emptyMessage="No weight readings yet."
				items={weightManage.items}
				onEdit={weightManage.onEdit}
				onDelete={weightManage.onDelete}
				deletingId={weightManage.deletingId}
				error={weightManage.error}
			/>
		</div>
	);
}
